import { Bullet, PoolFormattedMessage, PoolTypography } from '@synergy/components';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { Grid, Theme } from '@mui/material';
import { WithStyles, createStyles, withStyles } from '@mui/styles';
import { getDeliveryDateTypeTranslationId } from '@synergy/models';
import dayjs from 'dayjs';

import { AccountPurchase } from '../../common/models/account-purchase';
import { Inquiry } from '../../common/models/inquiry';

const styles = (theme: Theme) =>
    createStyles({
        container: {
            margin: '41px 0',
        },
        contentContainer: {
            display: 'flex',
            flexDirection: 'column',
        },
        customerHeader: {
            marginBottom: 11,
        },
        customerContainerHeader: {
            marginBottom: -4,
            color: theme.palette.grey[700],
        },
        customerInformation: {
            marginBottom: 17.5,
        },
        gridSpace: {
            marginTop: 40,
        },
        informationSize: {
            fontSize: 15,
        },
        deliveryAddress: {
            display: 'flex',
            flexDirection: 'column',
        },
        deliveryAddressNoteTitle: {
            marginBottom: 9,
        },
    });

interface OffersInformationTopProps extends WithStyles<typeof styles> {
    inquiry: Inquiry;
    accountPurchase: AccountPurchase;
    index: number;
}

const OffersInformationTopBase = (props: OffersInformationTopProps) => {
    const { classes, accountPurchase, index } = props;
    const { deliveryAddress, deliveryDateType } = props.accountPurchase;

    return (
        <div className={classes.container}>
            <Grid container={true}>
                <Grid item={true} xs={12} className={classes.contentContainer}>
                    <PoolTypography typography="h6" className={classes.customerHeader}>
                        <FormattedMessage id="app.common.customerHeadline" values={{ value: index + 1 }} />
                    </PoolTypography>

                    <PoolTypography
                        typography="caption"
                        component="span"
                        className={classes.customerContainerHeader}
                        id="app.table.column.customer"
                    />
                    <PoolTypography typography="body1" component="span" className={classes.customerInformation}>
                        {accountPurchase.customerAccount.name}
                    </PoolTypography>

                    <PoolTypography
                        typography="caption"
                        component="span"
                        className={classes.customerContainerHeader}
                        id="app.common.deliveryAddress"
                    />
                    <PoolTypography typography="body1">
                        <div className={`${classes.customerInformation} ${classes.deliveryAddress}`}>
                            {deliveryAddress.title && <div>{deliveryAddress.title}</div>}
                            <div>
                                {deliveryAddress.title && (
                                    <>
                                        {deliveryAddress.title} <Bullet />
                                    </>
                                )}
                                <span>{deliveryAddress.street}</span>
                                <Bullet />
                                <span>{deliveryAddress.number}</span>
                                <Bullet />
                                {deliveryAddress.addition && (
                                    <>
                                        {deliveryAddress.addition} <Bullet />
                                    </>
                                )}
                                <span>{deliveryAddress.zip}</span>
                                <Bullet />
                                <span>{deliveryAddress.city}</span>
                            </div>
                        </div>
                    </PoolTypography>

                    <PoolTypography
                        typography="caption"
                        component="span"
                        id="app.common.deliveryDate"
                        className={classes.customerContainerHeader}
                    />
                    <PoolTypography
                        typography="body1"
                        component="span"
                        style={{ marginBottom: deliveryAddress.note ? 8 : 0 }}
                    >
                        <PoolFormattedMessage
                            id="app.common.deliveryDateType"
                            values={{
                                deliveryDateType: (
                                    <FormattedMessage id={getDeliveryDateTypeTranslationId(deliveryDateType)} />
                                ),
                            }}
                        />
                        <FormattedDate
                            value={accountPurchase.deliveryDate}
                            year="numeric"
                            month="short"
                            day="2-digit"
                        />
                        <FormattedMessage
                            id="app.offers.deliveryDateCw"
                            values={{ value: dayjs(props.accountPurchase.deliveryDate).isoWeek() }}
                        />
                    </PoolTypography>
                </Grid>

                {deliveryAddress.note && (
                    <Grid item={true} xs={12} className={classes.gridSpace}>
                        <PoolTypography
                            typography="h6"
                            component="span"
                            id="app.common.note"
                            className={classes.deliveryAddressNoteTitle}
                        />
                        <PoolTypography typography="body1">{deliveryAddress.note}</PoolTypography>
                    </Grid>
                )}
            </Grid>
        </div>
    );
};

export const OffersInformationTop = withStyles(styles)(OffersInformationTopBase);
